import { Link } from 'react-router-dom';
import type { Actress } from '@/mocks/actresses';
import { actressImages } from '@/mocks/actressImages';

interface ActressCardProps {
  actress: Actress;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ActressCard({ actress, variant = 'default' }: ActressCardProps) {
  const imageUrl = actressImages[actress.slug] || '';

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${actress.slug}`}
        className="group flex items-center gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
          <img
            src={imageUrl}
            alt={`${actress.name} ${actress.nameJp} 日本女优`}
            className="w-full h-full object-cover object-top"
            title={`${actress.name} - ${actress.category}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors whitespace-nowrap">
              {actress.name}
            </span>
            {actress.hot && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium whitespace-nowrap">
                HOT
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-500 mt-0.5 truncate">{actress.description}</p>
        </div>
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${actress.slug}`}
        className="group relative rounded-xl overflow-hidden bg-background-100 cursor-pointer block"
      >
        <div className="aspect-[3/4] overflow-hidden">
          <img
            src={imageUrl}
            alt={`${actress.name} ${actress.nameJp} 日本女优`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            title={`${actress.name} - ${actress.category}`}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {actress.hot && (
            <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-accent-500 text-background-50 font-medium mb-2 whitespace-nowrap">
              热门推荐
            </span>
          )}
          <h3 className="text-base font-heading font-semibold text-white">{actress.name}</h3>
          <p className="text-xs text-white/70 mt-1">{actress.nameJp}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${actress.slug}`}
      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer block"
    >
      <div className="aspect-[3/4] overflow-hidden bg-background-100">
        <img
          src={imageUrl}
          alt={`${actress.name} ${actress.nameJp} 日本女优`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={`${actress.name} - ${actress.category}`}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
            {actress.name}
          </h3>
          {actress.hot && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 font-medium whitespace-nowrap">
              HOT
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 mb-2">{actress.nameJp}</p>
        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">{actress.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {actress.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}